import { appLog } from '@shared/common/logging/app-log.util';
import { Component, OnInit, OnDestroy, Input, Injector, NgZone, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppRoutes } from '@app/shared/routes/app-routes';
import { TimesheetReStructuringServiceProxy } from '@shared/service-proxies/service-proxies';
import { TimesheetReminderStateService, ReminderProject } from './timesheet-reminder-state.service';

interface ProjectAnimation {
    opacity: number;
    transform: string;
    transition: string;
}
@Component({
    standalone: false,
    selector: 'app-pending-timesheet-reminder',
    templateUrl: './pending-timesheet-reminder.component.html',
    styleUrls: ['./pending-timesheet-reminder.component.css']
})
export class PendingTimesheetReminderComponent extends AppComponentBase implements OnInit, OnDestroy {

    /** Identifies which page hosts this component. Used for per-page session skip state. */
    @Input() pageKey: string = '';
    @Input() showDebugControls: boolean = false;

    showNotification: boolean = false;
    dateDisplay: string = '';
    projects: ReminderProject[] = [];
    projectAnimations: ProjectAnimation[] = [];
    skipVisible: boolean = false;
    progressActive: boolean = false;
    progressPercent: number = 0;

    private _cacheSub: Subscription;
    private _progressInterval: any;
    private _downloadPopupHandler: (() => void) | null = null;

    constructor(
        injector: Injector,
        private router: Router,
        private _ngZone: NgZone,
        private _cdr: ChangeDetectorRef,
        private _timesheetService: TimesheetReStructuringServiceProxy,
        private _reminderState: TimesheetReminderStateService
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.dateDisplay = this.getYesterdayDate();

        // Subscribe before calling initialize() so a synchronous BehaviorSubject
        // emission (when the cache is already populated) is never missed.
        this._cacheSub = this._reminderState.cache$.subscribe(cache => {
            if (!cache.loaded) {
                return;
            }

            this.projects = cache.projects;
            this.projectAnimations = this.projects.map(() => ({
                opacity: 0,
                transform: 'translateY(10px)',
                transition: 'none'
            }));

            if (this.projects.length > 0) {
                // If the user already skipped this page in the current session, stay hidden.
                if (this._reminderState.isPageSkipped(this.pageKey)) {
                    this.showNotification = false;
                    return;
                }
                if (this._reminderState.isDownloadPopupActive) {
                    // Download Now popup is currently visible — wait for it to close first.
                    this._waitForDownloadPopup();
                } else {
                    this._showReminderPopup();
                }
            } else {
                this.showNotification = false;
            }
        });

        // No-op if the API has already been called; otherwise triggers the fetch.
        const userId = this.appSession.userId;
        if (userId) {
            this._reminderState.initialize(userId, this._timesheetService);
        }
    }

    ngOnDestroy(): void {
        if (this._cacheSub) {
            this._cacheSub.unsubscribe();
        }
        this._stopProgressTimer();
        this._cancelDownloadWait();
    }

    closeNotification(): void {
        this.showNotification = false;
        this._stopProgressTimer();
        this._cancelDownloadWait();
        this.progressActive = false;
        this.skipVisible = false;
    }

    fillTimesheet(): void {
        this._stopProgressTimer();
        this._cancelDownloadWait();
        this.progressActive = false;
        this.showNotification = false;

        // Reset the cache so the API re-checks after the user submits the timesheet.
        // If they submitted, the popup will not re-appear; if they didn't, it will.
        this._reminderState.reset();

        this.router.navigate([AppRoutes.timesheet], {
            queryParams: {
                tab: 'my-timesheet',
                fromReminder: 'true'
            }
        }).then((navigationResult) => {
            appLog.log('Navigation successful:', navigationResult);
        }).catch((error) => {
            appLog.error('Primary navigation failed:', error);
            this.router.navigate([AppRoutes.timesheet]).catch((fallbackError) => {
                appLog.error('Fallback navigation failed:', fallbackError);
                window.location.href = AppRoutes.timesheet;
            });
        });
    }

    private animateProjects(): void {
        this.projects.forEach((_, index) => {
            setTimeout(() => {
                this.projectAnimations[index] = {
                    opacity: 1,
                    transform: 'translateY(0)',
                    transition: 'all 0.3s ease'
                };
            }, 300 + (index * 80));
        });
    }

    private getYesterdayDate(): string {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const day = String(yesterday.getDate()).padStart(2, '0');
        const month = String(yesterday.getMonth() + 1).padStart(2, '0');
        const year = yesterday.getFullYear();
        return `${day}-${month}-${year}`;
    }

    skipReminder(): void {
        this._stopProgressTimer();
        this._cancelDownloadWait();
        this.progressActive = false;
        this.showNotification = false;
        // Persist the skip for this page so navigation / component recreation
        // does not re-show the popup during the current session.
        this._reminderState.skipPage(this.pageKey);
    }

    private _showReminderPopup(): void {
        this.showNotification = true;
        this.animateProjects();
        // Defer timer start by one microtask so the popup view is fully rendered
        // before measurements/animations begin, preventing stuck-progress issues.
        Promise.resolve().then(() => this.startProgressTimer());
    }

    private _waitForDownloadPopup(): void {
        // Cancel any previous pending handler to prevent duplicates.
        this._cancelDownloadWait();
        this._downloadPopupHandler = () => {
            this._cancelDownloadWait();
            if (!this._reminderState.isPageSkipped(this.pageKey) && this.projects.length > 0) {
                this._ngZone.run(() => this._showReminderPopup());
            }
        };
        abp.event.on('DownloadITPModalHide', this._downloadPopupHandler);
    }

    private _cancelDownloadWait(): void {
        if (this._downloadPopupHandler) {
            abp.event.off('DownloadITPModalHide', this._downloadPopupHandler);
            this._downloadPopupHandler = null;
        }
    }

    private _stopProgressTimer(): void {
        if (this._progressInterval) {
            clearInterval(this._progressInterval);
            this._progressInterval = null;
        }
    }

    private startProgressTimer(): void {
        this._stopProgressTimer();
        this.skipVisible = false;
        this.progressActive = true;
        this.progressPercent = 0;
        const totalMs = 5000;
        const tickMs = 100;
        const steps = totalMs / tickMs;
        let step = 0;
        // Run the interval OUTSIDE Angular's zone to avoid triggering change
        // detection on every tick. Each state update is then explicitly pushed
        // back INTO the zone via ngZone.run(), which also calls detectChanges()
        // to guarantee the view reflects the update regardless of CD strategy.
        this._ngZone.runOutsideAngular(() => {
            this._progressInterval = setInterval(() => {
                step++;
                const pct = Math.min(100, Math.round((step / steps) * 100));
                const done = step >= steps;
                this._ngZone.run(() => {
                    this.progressPercent = pct;
                    if (done) {
                        this._stopProgressTimer();
                        this.progressActive = false;
                        this.skipVisible = true;
                    }
                    this._cdr.detectChanges();
                });
            }, tickMs);
        });
    }

    getEnabledReminderProjectsCount(): number {
        return this.projects.filter(p => p.reminderEnabled).length;
    }

    getDisabledReminderProjectsCount(): number {
        return this.projects.filter(p => !p.reminderEnabled).length;
    }
}
